import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { AddTagModal } from './AddTagModal'
import type { TagPreviewResult } from './AddTagModal'
import type { AudienceTag, AudienceTagCondition } from '@digital-twins/shared-types'
import type { SegmentFilterQuestion } from '@/lib/utils/segment-filter-questions'

interface AudienceTagsSectionProps {
  tags: Array<AudienceTag>
  onTagsChange: (tags: Array<AudienceTag>) => void
  columnSamples: Record<string, string[]>
  columnLabels?: Record<string, string>
  columnValueLabels?: Record<string, Record<string, string>>
  /** Full filterable question universe (singles + groups) — mirrors the filter dropdown. */
  questions?: Array<SegmentFilterQuestion>
  /** When true, renders a compact muted label instead of the full section header */
  compact?: boolean
  /** When true, disables the Add tag button (e.g. while column data is still loading). */
  disabled?: boolean
  onPreviewTag?: (
    conditions: Array<AudienceTagCondition>,
    signal: AbortSignal,
  ) => Promise<TagPreviewResult>
}

export function AudienceTagsSection({
  tags,
  onTagsChange,
  columnSamples,
  columnLabels,
  columnValueLabels,
  questions,
  compact,
  disabled,
  onPreviewTag,
}: AudienceTagsSectionProps) {
  const { t } = useTranslation('app')
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [editingTag, setEditingTag] = useState<AudienceTag | null>(null)

  const validTags = useMemo(
    () => tags.filter((t): t is AudienceTag => Boolean(t) && typeof t === 'object' && !Array.isArray(t) && typeof t.id === 'string'),
    [tags],
  )

  const handleSave = useCallback(
    (tag: AudienceTag) => {
      const existing = tags.find((t) => t.id === tag.id)
      if (existing) {
        onTagsChange(tags.map((t) => (t.id === tag.id ? tag : t)))
      } else {
        onTagsChange([...tags, tag])
      }
      setEditingTag(null)
    },
    [tags, onTagsChange],
  )

  const handleRemove = useCallback(
    (tagId: string) => {
      onTagsChange(tags.filter((t) => t.id !== tagId))
    },
    [tags, onTagsChange],
  )

  const handleEdit = useCallback((tag: AudienceTag) => {
    setEditingTag(tag)
  }, [])

  const getColumnLabel = useMemo(() => {
    return (col: string) => columnLabels?.[col] ?? col
  }, [columnLabels])

  const getValueLabel = useMemo(() => {
    return (col: string, val: string) => {
      const labels = columnValueLabels?.[col]
      if (!labels) return val
      return labels[val] ?? val
    }
  }, [columnValueLabels])

  const addTagButton = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={validTags.length > 0 ? 'shrink-0' : 'self-start'}
      data-testid="audience-tags-add-button"
      disabled={disabled}
      onClick={() => {
        setEditingTag(null)
        setAddModalOpen(true)
      }}
    >
      <Plus className="mr-1.5 h-3.5 w-3.5" />
      {t('audienceCreate.addTag')}
    </Button>
  )

  return (
    <section
      className="flex flex-col gap-4"
      data-testid="audience-tags-section"
    >
      {compact ? (
        <>
          <p className="text-sm text-muted-foreground">{t('audienceCreate.audienceSubsegments')}</p>
          <TooltipProvider delayDuration={200}>
            <div className="flex min-h-[38px] items-center justify-between gap-3">
              <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
                {validTags.length === 0 ? (
                  <span className="text-base text-muted-foreground">{t('audienceCreate.noSubsegments')}</span>
                ) : validTags.map((tag) => (
                  <TagPill
                    key={tag.id}
                    tag={tag}
                    getColumnLabel={getColumnLabel}
                    getValueLabel={getValueLabel}
                    onEdit={handleEdit}
                    onRemove={handleRemove}
                  />
                ))}
              </div>
                <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 gap-2"
                data-testid="audience-tags-add-button"
                disabled={disabled}
                onClick={() => { setEditingTag(null); setAddModalOpen(true) }}
              >
                <Plus className="h-3.5 w-3.5" />
                {t('audienceCreate.addTag')}
              </Button>
            </div>
          </TooltipProvider>
        </>
      ) : (
        <>
          <header className="flex flex-col gap-1">
            <h2 className="text-lg font-medium text-foreground">{t('audienceCreate.audienceTags')}</h2>
            <p className="text-sm text-muted-foreground whitespace-pre-line">
              {t('audienceCreate.tagsHelper')}
            </p>
          </header>

          <TooltipProvider delayDuration={200}>
            {validTags.length > 0 ? (
              <div className="flex items-center gap-3">
                <div className="flex min-w-0 flex-wrap gap-2">
                  {validTags.map((tag) => (
                    <TagPill
                      key={tag.id}
                      tag={tag}
                      getColumnLabel={getColumnLabel}
                      getValueLabel={getValueLabel}
                      onEdit={handleEdit}
                      onRemove={handleRemove}
                    />
                  ))}
                </div>
                {addTagButton}
              </div>
            ) : (
              addTagButton
            )}
          </TooltipProvider>
        </>
      )}

      {(addModalOpen || editingTag != null) && (
        <AddTagModal
          open
          onOpenChange={(open) => {
            if (!open) {
              setAddModalOpen(false)
              setEditingTag(null)
            }
          }}
          columnSamples={columnSamples}
          columnLabels={columnLabels}
          columnValueLabels={columnValueLabels}
          questions={questions}
          existingTag={editingTag}
          onPreview={onPreviewTag}
          onSave={handleSave}
        />
      )}
    </section>
  )
}

function TagPill({
  tag,
  getColumnLabel,
  getValueLabel,
  onEdit,
  onRemove,
}: {
  tag: AudienceTag
  getColumnLabel: (col: string) => string
  getValueLabel: (col: string, val: string) => string
  onEdit: (tag: AudienceTag) => void
  onRemove: (tagId: string) => void
}) {
  const { t } = useTranslation('app')
  const conditions = Array.isArray(tag.conditions) ? tag.conditions : []
  const tooltipContent = conditions.map((c, i) => {
    const col = getColumnLabel(c.sourceColumn)
    const vals = (Array.isArray(c.values) ? c.values : [])
      .map((v) => getValueLabel(c.sourceColumn, v))
      .join(', ')
    return `${i > 0 ? t('audienceCreate.andPrefix') : ''}${col}: ${vals}`
  }).join('\n')

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className="flex items-center gap-2 rounded-full bg-primary/15 pl-4 pr-2 py-1.5"
          data-testid={`audience-tag-card-${tag.id}`}
        >
          <button
            type="button"
            className="text-base font-medium text-foreground leading-none hover:opacity-70 transition-opacity"
            onClick={() => onEdit(tag)}
            data-testid={`audience-tag-edit-${tag.id}`}
          >
            {tag.name}
          </button>
          <button
            type="button"
            className="flex h-5 w-5 items-center justify-center rounded-full hover:bg-foreground/10 text-foreground/50 hover:text-foreground transition-colors shrink-0"
            onClick={() => onRemove(tag.id)}
            data-testid={`audience-tag-remove-${tag.id}`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="start" className="max-w-sm whitespace-pre-line text-[14px] px-4 py-3">
        {tooltipContent}
      </TooltipContent>
    </Tooltip>
  )
}
